import { FC, useEffect, useRef, useState } from 'react';
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { useCountUp } from 'react-countup';

export interface Props {
  value: number;
  maxValue?: number;
  collapseRate?: number;
  collapsable?: boolean;
  displayMode?: 'none' | 'both' | 'left' | 'right';
  height?: number;
  borderRadius?: number;
  mainBackgroundColor?: string;
  mainColor?: string;
  openBackgroundColor?: string;
  openColor?: string;
  label?: string;
  decimals?: number;
  formattingFn?: (value: number) => string;
  sx?: SxProps<Theme>;
  sxBar?: SxProps<Theme>;
}

export const StatusBar: FC<Props> = ({
  value,
  collapsable = false,
  maxValue = 100,
  height = 20,
  collapseRate = 0.5,
  borderRadius,
  displayMode = 'left',
  mainBackgroundColor = 'heaven.main',
  mainColor = 'white',
  openBackgroundColor = 'mint.main',
  openColor = 'mint.dark',
  label,
  decimals = 0,
  formattingFn = (value) => `${value}%`,
  sx,
  sxBar
}) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsable ?? false);

  const handleMouseEnter = () => {
    if (collapsable) setIsCollapsed(false);
  };

  const handleMouseLeave = () => {
    if (collapsable) setIsCollapsed(true);
  };

  collapseRate = collapseRate <= 0 ? 0 : collapseRate > 1 ? 1 : collapseRate;

  const leftValue = value > maxValue ? maxValue : value < 0 ? 0 : value;
  const leftPrc = value <= 0 ? 0 : value / maxValue >= 1 ? 100 : (value / maxValue) * 100;

  const collapsedHeight = height * collapseRate;
  const rightPrcThreshold = 90;

  const displayLeftValue = displayMode == 'both' || displayMode == 'left';
  const displayRightValue = displayMode == 'both' || displayMode == 'right';

  const [prevValue, setPrevValue] = useState(0);
  const [usedWidth, setUsedWidth] = useState<string>();
  const usedWrapperRef = useRef<HTMLDivElement>(null);
  const usedRef = useRef<HTMLHeadingElement>(null);
  const unUsedRef = useRef(null);

  const usedcounter = useCountUp({
    ref: usedRef,
    start: prevValue,
    end: leftValue,
    formattingFn: formattingFn,
    duration: 1.5,
    decimals: decimals,
    startOnMount: false
  });
  const unUsedcounter = useCountUp({
    ref: unUsedRef,
    start: maxValue - prevValue,
    end: maxValue - leftValue,
    formattingFn: formattingFn,
    duration: 1.5,
    decimals: decimals,
    startOnMount: false
  });

  // dynamically set width of used bar
  useEffect(() => {
    if (usedWrapperRef.current && usedRef.current) {
      if (
        !isCollapsed &&
        usedWrapperRef.current.offsetWidth < usedRef.current.offsetWidth &&
        !['right', 'none'].includes(displayMode)
      ) {
        setUsedWidth('auto');
      } else {
        setUsedWidth(`${leftPrc}%`);
      }
    }
  }, [isCollapsed, leftPrc, displayMode, usedWrapperRef.current, usedRef.current]);

  useEffect(() => {
    usedcounter.getCountUp().startVal = prevValue;
    usedcounter.update(leftValue);
    usedcounter.start();
    unUsedcounter.getCountUp().startVal = prevValue;
    unUsedcounter.update(leftValue);
    setPrevValue(leftValue);
    unUsedcounter.start();
  }, [leftValue]);

  return (
    <Box sx={{ ...sx }}>
      {label && (
        <Typography variant="body2" color="grey.500" paddingBottom="8px">
          {label}
        </Typography>
      )}
      <Box
        onMouseEnter={() => handleMouseEnter()}
        onMouseLeave={() => handleMouseLeave()}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          height: `${isCollapsed ? collapsedHeight : height}px`,
          backgroundColor: openBackgroundColor,
          overflow: 'hidden',
          borderRadius: `${borderRadius ?? height}px`,
          transition: 'height .5s, border-radius .5s',
          ...sxBar
        }}
      >
        <Box
          ref={usedWrapperRef}
          sx={{
            width: usedWidth,
            height: `${isCollapsed ? collapsedHeight : height}px`,
            display: 'flex',
            backgroundColor: mainBackgroundColor,
            borderRadius: `${height}px`,
            transition: 'width .5s, height .5s, border-radius .5s'
          }}
        >
          <Typography
            variant="subtitle2"
            ref={usedRef}
            sx={{
              textAlign: 'left',
              color: mainColor,
              lineHeight: `${height * 0.5}px`,
              padding: `${height * 0.25}px`,
              msAlignSelf: 'stretch',
              display: displayLeftValue ? 'inline-block' : 'none',
              alignSelf: 'center',
              opacity: isCollapsed ? 0 : 1,
              transition: 'all .5s',
              width: `100%`
            }}
          />
        </Box>
        <Box
          sx={{
            width: `${100 - leftPrc}%`,
            height: `${isCollapsed ? collapsedHeight : height}px`,
            display: 'flex',
            justifyContent: 'flex-end',
            transition: 'width .5s, height .5s, border-radius .5s'
          }}
        >
          <Typography
            ref={unUsedRef}
            variant="subtitle2"
            sx={{
              textAlign: 'right',
              color: openColor,
              opacity: isCollapsed ? 0 : 1,
              lineHeight: `${height * 0.5}px`,
              padding: `${height * 0.25}px`,
              msAlignSelf: 'stretch',
              display: displayRightValue ? 'inline-block' : 'none',
              visibility: !isCollapsed && leftPrc <= rightPrcThreshold ? 'visible' : 'hidden',
              alignSelf: 'center',
              transition: 'all .5s'
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
